import React, {
    Context,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useMachine } from '@xstate/react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useDeepCompareCallback, useDeepCompareEffect } from 'use-deep-compare';
import axios, { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';
import _uniqBy from 'lodash/uniqBy';

import {
    formatYearAndQuarter,
    locale,
    getWebpackEnvVar,
    updateURLSearchParams,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import LIST_GAP_ANALYSIS_SUMMARIES from '@trqk/api/src/graphql/operations/listGapAnalysisSummaries.gql';
import {
    useDeleteGapAnalysisMutation,
    useListGapAnalysisPeriodSelectionsLazyQuery,
} from '@trqk/api';

import { gapAnalysisMachine } from '.';
import { useEntities, useToast, useLogData, useTokenModal } from '..';
import { MANAGE_GAP_ANALYSIS_HANDLER_KEY } from './constants';
import { GapAnalysisDownloadReady } from '../../components/toastMessages';
import { FULL_PATHS, URL_PARAMS } from '../../constants';

const { GET_SESSION_CURRENT_USER, GET_ACTIVE_LOG_POLLS } = queryStrings;

// The `detection_batch.channel_summary` channel array is deprecated; we now fetch it separately
// with the `listGapChannels` query.
const GET_GAP_ANALYSIS = gql`
    query getGapAnalysis($gap_analysis_id: String!) {
        getGapAnalysis(gap_analysis_id: $gap_analysis_id) {
            date_created
            description
            detection_batch {
                audio_count
                audio_count_raw
                audios_detected_raw
                channel_count
                channel_count_raw
                detection_count
                detection_count_raw
                detection_range_end
                detection_range_raw_end
                detection_range_raw_start
                detection_range_start
                detection_source
            }
            entity_id
            gap_analysis_id
            performance_period
            performance_period_end
            performance_period_start
            pro
            statement_id_international
            statement_id_standard
            user_id
        }
    }
`;

// This is the preferred way to fetch gap channels - do not reference them from within
// `trqkGap.detection_batch.channel_summary`; that array is deprecated.
// We do not include the `channel_audios[n].match_options` here because it will not be available
// until the channel has `channel_status: ANALYZED`. We rely on the `listGapChannelAudios` query to
// fetch the `match_options` data.
const LIST_GAP_CHANNELS = gql`
    query listGapChannels($gap_analysis_id: String!, $included: Boolean) {
        listGapChannels(
            gap_analysis_id: $gap_analysis_id
            included: $included
        ) {
            ads_promos_count_total
            audios_detected_raw
            audios_included
            channel_id
            channel_name_detection
            channel_name_statement
            channel_status
            detections_included
            detections_raw
            estimated_loss_max
            estimated_loss_min
            include
            payments_matched_total
            performances_matched
            performances_matched_total
            performances_raw
            programs_detected
        }
    }
`;

const LIST_GAP_CHANNEL_AUDIOS = gql`
    query listGapChannelAudios(
        $gap_analysis_id: String!
        $channel_id: String!
        $included: Boolean
    ) {
        listGapChannelAudios(
            gap_analysis_id: $gap_analysis_id
            channel_id: $channel_id
            included: $included
        ) {
            gap_audio_id
            detection_artist
            detection_batch_id
            detection_count
            detection_source
            detection_title
            estimated_loss_max
            estimated_loss_min
            include
            match_options {
                gap_audio_id
                song_id
                work_performances
                work_survey_type
                work_title
                work_use_code
            }
            matched_work_id
            matched_work_performances
            matched_work_survey_type
            matched_work_title
            matched_work_use_code
        }
    }
`;

const EXPORT_GAP_ANALYSIS = `
    mutation exportGapAnalysis($gap_analysis_id: String!, $export_filename: String) {
        exportGapAnalysis(gap_analysis_id: $gap_analysis_id, export_filename: $export_filename) {
            gap_analysis_id
        }
    }
`;

interface GapAnalysisToolsProviderProps {
    children: ReactNode;
    GapAnalysisToolsContext: Context<GapAnalysisToolsProviderValue>;
}

/**
 * Returns data and methods for creating and editing Gap Analysis reports. Also manages logic around
 * log events that relate to current gap analysis processes. Note that log polling intervals are
 * set by the ManageGapAnalysis modal according to whether it is mounted or not - see:
 * packages/web/src/components/modals/ManageGapAnalysis/ManageGapAnalysis.tsx
 * @param props - {@link GapAnalysisToolsProviderProps}
 */
export const GapAnalysisToolsProvider = ({
    children,
    GapAnalysisToolsContext,
}: GapAnalysisToolsProviderProps): JSX.Element => {
    const client = useApolloClient();
    const { add: addToast } = useToast();
    const { addHandler, deleteHandlers } = useLogData();
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { getToken, getTokenAsync } = useTokenModal();
    const history = useHistory();
    const [state, send] = useMachine<GapAnalysisContext, GapAnalysisEvent>(
        gapAnalysisMachine,
    );
    const { context } = state;
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const currentUserId = currentUserQuery?.data?.session?.currentUser?.id;
    const { selectedEntityId } = useEntities();
    const [gapSummariesRefreshTime, setGapSummariesRefreshTime] = useState(0);
    const [gapSummaries, setGapSummaries] = useState<TrqkGapSummary[]>([]);

    const fetchGapSummaries = useCallback(
        (): Promise<boolean> =>
            new Promise(resolve => {
                if (currentUserId && selectedEntityId) {
                    client
                        .query<{ listGapAnalysisSummaries: TrqkGapSummary[] }>({
                            query: LIST_GAP_ANALYSIS_SUMMARIES,
                            variables: {
                                user_id: currentUserId,
                                entity_id: selectedEntityId,
                            },
                            fetchPolicy: 'network-only',
                        })
                        .then(result => {
                            const nextTrqkGapSummaries =
                                result?.data?.listGapAnalysisSummaries ?? null;

                            if (nextTrqkGapSummaries) {
                                setGapSummaries(nextTrqkGapSummaries);
                                setGapSummariesRefreshTime(Date.now());
                                resolve(true);
                            } else {
                                addToast({
                                    kind: 'warning-banner',
                                    content:
                                        locale.enUS.API_ERRORS
                                            .listGapAnalysisSummaries,
                                    logMessage: 'listGapAnalysisSummaries',
                                });
                                resolve(false);
                            }
                        })
                        .catch(() => {
                            addToast({
                                kind: 'warning-banner',
                                content:
                                    locale.enUS.API_ERRORS
                                        .listGapAnalysisSummaries,
                                logMessage: 'listGapAnalysisSummaries',
                            });

                            resolve(false);
                        });
                } else {
                    resolve(false);
                }
            }),
        [currentUserId, selectedEntityId],
    );

    useEffect(() => {
        if (currentUserId && selectedEntityId) {
            fetchGapSummaries();
        }
    }, [currentUserId, selectedEntityId]);

    const [listGapAnalysisPeriodSelections] =
        useListGapAnalysisPeriodSelectionsLazyQuery({
            fetchPolicy: 'network-only',
            onCompleted: data => {
                const periodSelections =
                    data?.listGapAnalysisPeriodSelections ?? null;

                if (periodSelections === null) {
                    addToast({
                        kind: 'warning-banner',
                        content:
                            locale.enUS.MANAGE_GAP_ANALYSIS.ERROR_MESSAGES
                                .LIST_PERIOD_SELECTIONS,
                        logMessage: 'listGapAnalysisPeriodSelections',
                    });
                } else {
                    send({
                        type: 'SET_PERIOD_SELECTIONS',
                        value: {
                            periodSelections,
                        },
                    });
                }

                send({
                    type: 'SET_LOADING',
                    value: {
                        loading: false,
                    },
                });
            },
            onError: () => {
                addToast({
                    kind: 'warning-banner',
                    content:
                        locale.enUS.MANAGE_GAP_ANALYSIS.ERROR_MESSAGES
                            .LIST_PERIOD_SELECTIONS,
                    logMessage: 'listGapAnalysisPeriodSelections',
                });
                send({
                    type: 'SET_LOADING',
                    value: {
                        loading: false,
                    },
                });
            },
        });

    const fetchPeriodSelections = useCallback(
        (userId: string, entityId: string) => {
            send({
                type: 'SET_LOADING',
                value: {
                    loading: true,
                },
            });

            listGapAnalysisPeriodSelections({
                variables: {
                    user_id: userId,
                    entity_id: entityId,
                },
            });
        },
        [],
    );

    // Ensure that the `context.periodSelections` list is up to date whenever the userId or entityId
    // changes
    useEffect(() => {
        if (currentUserId && selectedEntityId) {
            fetchPeriodSelections(currentUserId, selectedEntityId);
        }
    }, [currentUserId, selectedEntityId, fetchPeriodSelections]);

    const performancePeriod = context?.trqkGap?.performance_period ?? null;
    const { selectedPeriod, periodSelections } = context;

    // Ensure that there is a `context.selectedPeriod` if there is a `context.trqkGap`.
    useDeepCompareEffect(() => {
        if (
            performancePeriod !== null &&
            selectedPeriod === null &&
            periodSelections !== null
        ) {
            const nextSelectedPeriod = periodSelections.find(
                item =>
                    formatYearAndQuarter(item.performance_period_label) ===
                    formatYearAndQuarter(performancePeriod),
            );

            if (nextSelectedPeriod) {
                send({
                    type: 'SET_SELECTED_PERIOD',
                    value: {
                        selectedPeriod: nextSelectedPeriod,
                    },
                });
            }
        }
    }, [performancePeriod, selectedPeriod, periodSelections, gapSummaries]);

    const fetchAndSetTrqkGap = useCallback(
        (gapAnalysisId: string): Promise<TrqkGap | null> =>
            new Promise(resolve => {
                client
                    .query<{ getGapAnalysis: TrqkGap }>({
                        query: GET_GAP_ANALYSIS,
                        variables: {
                            gap_analysis_id: gapAnalysisId,
                        },
                        fetchPolicy: 'network-only',
                    })
                    .then(result => {
                        const updatedTrqkGap =
                            result?.data?.getGapAnalysis ?? null;

                        if (updatedTrqkGap) {
                            resolve(updatedTrqkGap);

                            send({
                                type: 'SET_TRQK_GAP',
                                value: {
                                    trqkGap: updatedTrqkGap,
                                },
                            });
                        } else {
                            addToast({
                                kind: 'warning-banner',
                                content: locale.enUS.API_ERRORS.getGapAnalysis,
                            });

                            resolve(null);
                        }
                    })
                    .catch(() => {
                        addToast({
                            kind: 'warning-banner',
                            content: locale.enUS.API_ERRORS.getGapAnalysis,
                        });

                        resolve(null);
                    });
            }),
        [],
    );

    const fetchAndSetChannels = useCallback(
        (
            gapAnalysisId: string,
            included?: boolean,
        ): Promise<Maybe<TrqkGapChannel[]>> =>
            new Promise(resolve => {
                client
                    .query<{ listGapChannels: TrqkGapChannel[] }>({
                        query: LIST_GAP_CHANNELS,
                        variables: {
                            gap_analysis_id: gapAnalysisId,
                            included,
                        },
                        fetchPolicy: 'network-only',
                    })
                    .then(result => {
                        const updatedChannels =
                            result?.data?.listGapChannels ?? null;

                        send({
                            type: 'SET_CHANNELS',
                            value: {
                                channels: updatedChannels ?? [],
                            },
                        });

                        if (updatedChannels) {
                            resolve(updatedChannels);
                        } else {
                            addToast({
                                kind: 'warning-banner',
                                content: locale.enUS.API_ERRORS.listGapChannels,
                                logMessage: 'listGapChannels',
                            });

                            resolve(null);
                        }
                    })
                    .catch(() => {
                        addToast({
                            kind: 'warning-banner',
                            content: locale.enUS.API_ERRORS.listGapChannels,
                            logMessage: 'listGapChannels',
                        });

                        resolve(null);
                    });
            }),
        [],
    );

    const fetchAndUpdateAudioLists = useCallback(
        (
            gapAnalysisId: string,
            channelId: string,
            included?: boolean,
        ): Promise<Maybe<TrqkGapChannelAudio[]>> =>
            new Promise(resolve => {
                client
                    .query<{ listGapChannelAudios: TrqkGapChannelAudio[] }>({
                        query: LIST_GAP_CHANNEL_AUDIOS,
                        variables: {
                            gap_analysis_id: gapAnalysisId,
                            channel_id: channelId,
                            included,
                        },
                        fetchPolicy: 'network-only',
                    })
                    .then(result => {
                        const nextAudios =
                            result?.data?.listGapChannelAudios ?? null;

                        if (nextAudios === null) {
                            resolve(null);
                        } else {
                            // ensure that the match_options list is unique by song_id
                            const dedupedNextAudios = nextAudios.map(audio => {
                                const dedupedMatchOptions = _uniqBy(
                                    audio.match_options,
                                    'song_id',
                                );

                                return {
                                    ...audio,
                                    match_options: dedupedMatchOptions,
                                };
                            });

                            send({
                                type: 'UPDATE_AUDIO_LISTS',
                                value: {
                                    channelId,
                                    audios: dedupedNextAudios,
                                },
                            });

                            resolve(dedupedNextAudios);
                        }
                    })
                    .catch(() => {
                        resolve(null);
                    });
            }),
        [],
    );

    const fetchGapReportAudios = useCallback(
        async (gapAnalysisId: string): Promise<Maybe<GapReportAudio[]>> => {
            const fetchAudiosForChannel = async (
                channelId: string,
            ): Promise<Maybe<TrqkGapChannelAudio[]>> => {
                const token = await getTokenAsync();

                return axios({
                    method: 'post',
                    url: trqkDirectoryUrl,
                    headers: {
                        Authorization: token,
                    },
                    data: {
                        query: `
                            query listGapChannelAudios(
                                $gap_analysis_id: String!
                                $channel_id: String!
                                $included: Boolean
                            ) {
                                listGapChannelAudios(
                                    gap_analysis_id: $gap_analysis_id
                                    channel_id: $channel_id
                                    included: $included
                                ) {
                                    gap_audio_id
                                    audio_filename
                                    detection_count
                                    detection_title
                                    estimated_loss_max
                                    estimated_loss_min
                                    matched_work_id
                                    matched_work_title
                                    matched_work_performances
                                    matched_work_program
                                    matched_work_series
                                    matched_work_survey_type
                                    matched_work_use_code
                                    match_options {
                                        gap_audio_id
                                        song_id
                                        work_performances
                                        work_survey_type
                                        work_title
                                        work_use_code
                                    }
                                }
                            }
                        `,
                        variables: {
                            gap_analysis_id: gapAnalysisId,
                            channel_id: channelId,
                            included: true,
                        },
                    },
                })
                    .then(
                        (
                            response: AxiosResponse<{
                                data: {
                                    listGapChannelAudios: TrqkGapChannelAudio[];
                                };
                            }>,
                        ) => {
                            const audiosList =
                                response?.data?.data?.listGapChannelAudios ??
                                null;

                            return audiosList;
                        },
                    )
                    .catch(() => null);
            };

            const fetchChannels = async (): Promise<
                Maybe<TrqkGapChannel[]>
            > => {
                const token = await getTokenAsync();

                return axios({
                    method: 'post',
                    url: trqkDirectoryUrl,
                    headers: {
                        Authorization: token,
                    },
                    data: {
                        query: `
                            query listGapChannels($gap_analysis_id: String!, $included: Boolean) {
                                listGapChannels(
                                    gap_analysis_id: $gap_analysis_id
                                    included: $included
                                ) {
                                    channel_id
                                    channel_name_detection
                                }
                            }
                        `,
                        variables: {
                            gap_analysis_id: gapAnalysisId,
                            included: true,
                        },
                    },
                })
                    .then(
                        (
                            response: AxiosResponse<{
                                data: {
                                    listGapChannels: TrqkGapChannel[];
                                };
                            }>,
                        ) => response?.data?.data?.listGapChannels ?? null,
                    )
                    .catch(() => null);
            };

            const channels = await fetchChannels();

            if (channels === null) {
                return null;
            }

            const fetchAudiosPromises = channels.map(
                ({
                    channel_id,
                    channel_name_detection,
                }: TrqkGapChannel): Promise<GapReportAudio[]> =>
                    new Promise((resolve, reject) => {
                        fetchAudiosForChannel(channel_id).then(
                            audiosForChannel => {
                                if (audiosForChannel === null) {
                                    reject(
                                        new Error(
                                            'No audios fetched for channel',
                                        ),
                                    );
                                } else {
                                    const gapReportAudios =
                                        audiosForChannel.map(
                                            audioForChannel => ({
                                                ...audioForChannel,
                                                channel_name_detection,
                                            }),
                                        );

                                    resolve(gapReportAudios);
                                }
                            },
                        );
                    }),
            );

            const { gapReportAudios, errors } = await Promise.allSettled(
                fetchAudiosPromises,
            )
                .then(results =>
                    results.reduce(
                        (
                            acc: {
                                gapReportAudios: GapReportAudio[];
                                errors: Error[];
                            },
                            curr: PromiseSettledResult<GapReportAudio[]>,
                        ) => {
                            if (curr.status === 'fulfilled') {
                                return {
                                    ...acc,
                                    gapReportAudios: [
                                        ...acc.gapReportAudios,
                                        ...curr.value,
                                    ],
                                };
                            }

                            return {
                                ...acc,
                                errors: [...acc.errors, curr.reason as Error],
                            };
                        },
                        {
                            gapReportAudios: [],
                            errors: [],
                        },
                    ),
                )
                .catch(() => ({
                    gapReportAudios: [],
                    errors: [],
                }));

            if (errors.length > 0) {
                addToast({
                    kind: 'warning-banner',
                    content:
                        locale.enUS.API_ERRORS
                            .listGapChannelAudiosForGapReportAudios,
                    logMessage: 'listGapChannelAudiosForGapReportAudios',
                });
            }

            return gapReportAudios.length > 0 ? gapReportAudios : null;
        },
        [],
    );

    /**
     * This event is called `detectionBatchAddedToGap`, but the event is triggered by an
     * `analyzeTrqkGap` mutation
     */
    const handleDetectionBatchAddedToGap = useCallback((event: LogEvent) => {
        const logDetail = event.detail ?? null;
        const gapAnalysisId = logDetail?.gap_id ?? null;

        if (
            event.eventStatus === 'success' ||
            event.eventStatus === 'failure'
        ) {
            if (logDetail && gapAnalysisId) {
                send({
                    type: 'ADD_LOG_DETAIL',
                    value: {
                        gapAnalysisId,
                        logDetail,
                    },
                });
            }
        }

        if (event.eventStatus === 'failure') {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.LOG_EVENT_ERRORS.detectionBatchAddedToGap,
                logMessage: 'detectionBatchAddedToTrqkGap',
            });
        }
    }, []);

    const [deleteGapAnalysisMutation] = useDeleteGapAnalysisMutation();

    const deleteGapAnalysis = useDeepCompareCallback(
        async (gapAnalysisId: string): Promise<string> => {
            const result = await deleteGapAnalysisMutation({
                variables: {
                    gap_analysis_id: gapAnalysisId,
                },
            });

            const deletedGapAnalysisId =
                result?.data?.deleteGapAnalysis?.gap_analysis_id ?? null;

            if (deletedGapAnalysisId) {
                const nextTrqkGapSummaries = gapSummaries.filter(
                    trqkGapSummary =>
                        trqkGapSummary.gap_analysis_id !== deletedGapAnalysisId,
                );

                setGapSummaries(nextTrqkGapSummaries);
                setGapSummariesRefreshTime(Date.now());

                return deletedGapAnalysisId;
            }

            const errorMessage = result.errors
                ? JSON.stringify(result.errors)
                : 'Error during deleteGapAnalysis';

            throw new Error(errorMessage);
        },
        [gapSummaries],
    );

    const [exportingGapAnalysisIds, setExportingGapAnalysisIds] = useState<
        string[]
    >([]);

    const [
        exportingGapAnalysisIdsWithFilename,
        setExportingGapAnalysisIdsWithFilename,
    ] = useState<string[]>([]);

    const [didShowDelayMessage, setDidShowDelayMessage] = useState(false);

    const exportGapAnalysis = useCallback(
        async (
            gapAnalysisId: string,
            exportFilename?: string,
        ): Promise<Maybe<string>> => {
            if (exportFilename === undefined) {
                setExportingGapAnalysisIds(prev => [...prev, gapAnalysisId]);
            } else {
                setExportingGapAnalysisIdsWithFilename(prev => [
                    ...prev,
                    gapAnalysisId,
                ]);
            }

            // Show the message about report exports taking a long time only once per page load
            if (!didShowDelayMessage) {
                addToast({
                    kind: 'info-banner',
                    content:
                        locale.enUS.MANAGE_GAP_ANALYSIS.INFO_MESSAGES
                            .REPORT_BEING_GENERATED,
                    logMessage: 'exportGapAnalysis',
                });

                setDidShowDelayMessage(true);
            }

            const token = await getTokenAsync();

            // For some reason, we get an invariant violation if we run this mutation with
            // @apollo/client's `client.query` method, so I'm using axios instead.
            const result = await axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                data: {
                    query: EXPORT_GAP_ANALYSIS,
                    variables: {
                        gap_analysis_id: gapAnalysisId,
                        export_filename: exportFilename,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<{
                            data: {
                                exportGapAnalysis: ExportGapAnalysisResult;
                            };
                        }>,
                    ) => {
                        const exportedGapAnalysisId =
                            response?.data?.data?.exportGapAnalysis
                                ?.gap_analysis_id ?? null;

                        if (exportedGapAnalysisId) {
                            return exportedGapAnalysisId;
                        }

                        addToast({
                            kind: 'warning-banner',
                            content: locale.enUS.API_ERRORS.exportGapAnalysis,
                            logMessage: 'exportGapAnalysis',
                        });

                        if (exportFilename === undefined) {
                            setExportingGapAnalysisIds(prev =>
                                prev.filter(id => id !== gapAnalysisId),
                            );
                        } else {
                            setExportingGapAnalysisIdsWithFilename(prev =>
                                prev.filter(id => id !== gapAnalysisId),
                            );
                        }

                        return null;
                    },
                )
                .catch(() => {
                    addToast({
                        kind: 'warning-banner',
                        content: locale.enUS.API_ERRORS.exportGapAnalysis,
                        logMessage: 'exportGapAnalysis',
                    });

                    if (exportFilename === undefined) {
                        setExportingGapAnalysisIds(prev =>
                            prev.filter(id => id !== gapAnalysisId),
                        );
                    } else {
                        setExportingGapAnalysisIdsWithFilename(prev =>
                            prev.filter(id => id !== gapAnalysisId),
                        );
                    }

                    return null;
                });

            return result;
        },
        [didShowDelayMessage],
    );

    useEffect(() => {
        // When there are one or more reports being exported, ensure that log events are polled on
        // the short interval
        if (exportingGapAnalysisIds.length > 0) {
            client.cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            exportGapAnalysis: true,
                        },
                    },
                },
            });
        } else {
            client.cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            exportGapAnalysis: false,
                        },
                    },
                },
            });
        }
    }, [exportingGapAnalysisIds.length]);

    const handleGapAnalysisExported = useDeepCompareCallback(
        (event: LogEvent) => {
            const logDetail = event.detail ?? null;
            const gapAnalysisId = logDetail?.gap_analysis_id ?? null;
            const exportFilename = logDetail?.export_filename ?? null;
            const reportUrl = logDetail?.download_report_url ?? null;

            if (event.eventStatus === 'success' && gapAnalysisId && reportUrl) {
                const gapSummary =
                    gapSummaries.find(
                        item => item.gap_analysis_id === gapAnalysisId,
                    ) ?? null;

                if (exportFilename) {
                    setExportingGapAnalysisIdsWithFilename(prev =>
                        prev.filter(
                            exportingGapAnalysisId =>
                                exportingGapAnalysisId !== gapAnalysisId,
                        ),
                    );
                } else {
                    setExportingGapAnalysisIds(prev =>
                        prev.filter(
                            exportingGapAnalysisId =>
                                exportingGapAnalysisId !== gapAnalysisId,
                        ),
                    );
                }

                addToast({
                    kind: 'info-card',
                    RenderContent: GapAnalysisDownloadReady,
                    renderContentInput: {
                        gapAnalysisId,
                        reportUrl,
                        gapSummary,
                    },
                    persist: true,
                });
            }

            if (event.eventStatus === 'failure' && gapAnalysisId) {
                if (exportFilename) {
                    setExportingGapAnalysisIdsWithFilename(prev =>
                        prev.filter(
                            exportingGapAnalysisId =>
                                exportingGapAnalysisId !== gapAnalysisId,
                        ),
                    );
                } else {
                    setExportingGapAnalysisIds(prev =>
                        prev.filter(
                            exportingGapAnalysisId =>
                                exportingGapAnalysisId !== gapAnalysisId,
                        ),
                    );
                }

                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.LOG_EVENT_ERRORS.gapAnalysisExported,
                    logMessage: 'gapAnalysisExported',
                });
            }
        },
        [gapSummaries],
    );

    useEffect(() => {
        addHandler({
            eventTypes: ['detectionBatchAddedToGap'],
            handler: handleDetectionBatchAddedToGap,
            handlerKey: MANAGE_GAP_ANALYSIS_HANDLER_KEY,
        });

        addHandler({
            eventTypes: ['gapAnalysisExported'],
            handler: handleGapAnalysisExported,
            handlerKey: MANAGE_GAP_ANALYSIS_HANDLER_KEY,
        });

        return () => {
            // When component unmounts, remove its log event handlers
            deleteHandlers(MANAGE_GAP_ANALYSIS_HANDLER_KEY);
        };
    }, [handleDetectionBatchAddedToGap, handleGapAnalysisExported]);

    const openManageGapAnalysis = useCallback(
        (input: OpenManageGapAnalysisInput) => {
            if (input.mode === 'edit') {
                const nextSearch = updateURLSearchParams(
                    history.location.search,
                    [
                        {
                            key: URL_PARAMS.GAP_ANALYSIS_ID,
                            value: input.gapAnalysisId,
                        },
                        {
                            key: URL_PARAMS.GAP_ANALYSIS_MODE,
                            value: input.mode,
                        },
                    ],
                );

                history.push({
                    search: nextSearch,
                    pathname: FULL_PATHS.MANAGE_GAP_ANALYSIS,
                });
            } else if (input.mode === 'create') {
                const nextSearch = updateURLSearchParams(
                    history.location.search,
                    [
                        {
                            key: URL_PARAMS.GAP_ANALYSIS_MODE,
                            value: input.mode,
                        },
                    ],
                );

                history.push({
                    search: nextSearch,
                    pathname: FULL_PATHS.MANAGE_GAP_ANALYSIS,
                });
            }
        },
        [history.push, history.location.search, send],
    );

    const startGapAnalysisEdit = useCallback(
        (gapAnalysisId: string) => {
            send({
                type: 'SET_MODE',
                value: {
                    mode: 'edit',
                },
            });

            send({
                type: 'SET_STEP_HISTORY',
                value: {
                    stepHistory: ['selectPeriod', 'setUp', 'confirmChannels'],
                },
            });

            send({
                type: 'SET_LOADING',
                value: {
                    loading: true,
                },
            });

            Promise.all([
                fetchAndSetTrqkGap(gapAnalysisId),
                fetchAndSetChannels(gapAnalysisId),
            ]).then(([nextGap, nextChannels]) => {
                if (nextGap && nextChannels) {
                    send({
                        type: 'UPDATE_STEP',
                        value: {
                            stepId: 'selectPeriod',
                            update: {
                                title: formatYearAndQuarter(
                                    nextGap.performance_period,
                                ),
                            },
                        },
                    });

                    setTimeout(() => {
                        send({
                            type: 'SET_LOADING',
                            value: {
                                loading: false,
                            },
                        });
                    }, 500);
                }
            });
        },
        [send, fetchAndSetTrqkGap, fetchAndSetChannels],
    );

    const openViewGapAnalysis = useCallback(
        (gapAnalysisId: string) => {
            const nextSearch = updateURLSearchParams(history.location.search, [
                {
                    key: URL_PARAMS.GAP_ANALYSIS_ID,
                    value: gapAnalysisId,
                },
            ]);

            history.push({
                search: nextSearch,
                pathname: FULL_PATHS.VIEW_GAP_ANALYSIS,
            });
        },
        [history.push, history.location.search],
    );

    const { reportDescription, trqkGap } = context;

    const detectionBatchStatus = trqkGap?.detection_batch_status ?? null;

    const hasAllowedStatus =
        detectionBatchStatus === null || detectionBatchStatus === 'ADDED';

    /**
     * Call `analyzeTrqkGap` and resolve to true if the mutation succeeded.
     */
    const analyzeTrqkGap = useCallback(
        (
            gapAnalysisId: Maybe<string>,
            filters: GapAnalysisFilterInputArray,
        ): Promise<boolean> =>
            new Promise(resolve => {
                if (
                    gapAnalysisId === null ||
                    reportDescription.length < 1 ||
                    !hasAllowedStatus
                ) {
                    resolve(false);
                } else {
                    client.cache.writeQuery({
                        query: GET_ACTIVE_LOG_POLLS,
                        data: {
                            uiState: {
                                __typename: 'UIState',
                                activeLogPolls: {
                                    __typename: 'ActiveLogPolls',
                                    analyzeTrqkGap: true,
                                },
                            },
                        },
                    });

                    getToken(token => {
                        axios({
                            method: 'post',
                            url: trqkDirectoryUrl,
                            headers: {
                                Authorization: token,
                            },
                            data: {
                                query: `
                                    mutation analyzeTrqkGap($gap_analysis_id: String!, $description: String, $filters: gapAnalysisFilterInputArray) {
                                        analyzeTrqkGap(gap_analysis_id: $gap_analysis_id, description: $description, filters: $filters) {
                                            date_created
                                            description
                                            entity_id
                                            gap_analysis_id
                                            performance_period
                                            performance_period_end
                                            performance_period_start
                                            statement_id_international
                                            statement_id_standard
                                            user_id
                                        }
                                    }
                                `,
                                variables: {
                                    gap_analysis_id: gapAnalysisId,
                                    description: reportDescription,
                                    filters,
                                },
                            },
                        })
                            .then(
                                (
                                    response: AxiosResponse<{
                                        data: {
                                            analyzeTrqkGap: TrqkGap;
                                        };
                                        errors?: Error[];
                                    }>,
                                ) => {
                                    client.cache.writeQuery({
                                        query: GET_ACTIVE_LOG_POLLS,
                                        data: {
                                            uiState: {
                                                __typename: 'UIState',
                                                activeLogPolls: {
                                                    __typename:
                                                        'ActiveLogPolls',
                                                    analyzeTrqkGap: false,
                                                },
                                            },
                                        },
                                    });

                                    if (response.data?.errors) {
                                        addToast({
                                            kind: 'warning-banner',
                                            content:
                                                locale.enUS.API_ERRORS
                                                    .analyzeTrqkGap,
                                        });
                                        resolve(false);
                                    } else {
                                        resolve(true);
                                    }
                                },
                            )
                            .catch(() => {
                                client.cache.writeQuery({
                                    query: GET_ACTIVE_LOG_POLLS,
                                    data: {
                                        uiState: {
                                            __typename: 'UIState',
                                            activeLogPolls: {
                                                __typename: 'ActiveLogPolls',
                                                analyzeTrqkGap: false,
                                            },
                                        },
                                    },
                                });

                                addToast({
                                    kind: 'warning-banner',
                                    content:
                                        locale.enUS.API_ERRORS.analyzeTrqkGap,
                                });

                                resolve(false);
                            });
                    });
                }
            }),
        [hasAllowedStatus, reportDescription],
    );

    return (
        <GapAnalysisToolsContext.Provider
            value={{
                analyzeTrqkGap,
                context,
                deleteGapAnalysis,
                exportGapAnalysis,
                exportingGapAnalysisIds,
                exportingGapAnalysisIdsWithFilename,
                fetchAndSetChannels,
                fetchAndSetTrqkGap,
                fetchAndUpdateAudioLists,
                fetchGapReportAudios,
                fetchGapSummaries,
                fetchPeriodSelections,
                gapSummaries,
                gapSummariesRefreshTime,
                openManageGapAnalysis,
                openViewGapAnalysis,
                send,
                startGapAnalysisEdit,
            }}
        >
            {children}
        </GapAnalysisToolsContext.Provider>
    );
};
